import React, { useCallback, useState } from 'react';

import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import Button from 'components/button/button';

import type { AvailableSingleCallJob } from 'state/remote/types';

import useFreelancerAcceptJob from 'state/remote/remote-freelancer/use-freelancer-accept-job';

import useFreelancerRefuseJob from 'state/remote/remote-freelancer/use-freelancer-refuse-job';

import useFreelancerInterestedInJob from 'state/remote/remote-freelancer/use-freelancer-interested-in-job';

import useAddAvailableDay from 'state/remote/remote-freelancer/use-add-available-day';

import {
  formatJobDurationDisplay,
  formatDateToLocaleNetherlands,
  formatRates,
} from 'utils/display-text-formatter';

import { createLocale } from 'locales';

import { showErrorToast } from 'components/toast/toast';

import ConfirmationDialog from '../confirmationDialog/ConfirmationDialog';

import Panel from '../collapsiblePanel/Panel';

import InfoItem from './InfoItem';

import CardHeader from './CardHeader';

const localize = createLocale('freelancerAvailableJobsLocales');
const globalLocalize = createLocale('global');

interface PropTypes {
  job?: AvailableSingleCallJob;
  vatExempt: boolean;
}

interface formatRateReturnType {
  displayText: string;
  useHighlight: boolean;
}

const JobCard: React.FC<PropTypes> = ({ job, vatExempt }) => {
  let salaryDisplay: formatRateReturnType = {} as formatRateReturnType;
  let headerTitle = '';
  const [BtnDisabled, setBtnDisabled] = useState(false);

  const [action, setAction] = useState<
    'accept' | 'refuse' | 'interest' | 'setAvailability' | undefined
  >(undefined);
  const acceptJob = useFreelancerAcceptJob(setBtnDisabled);
  const refuseJob = useFreelancerRefuseJob();
  const interestInJob = useFreelancerInterestedInJob();

  const availabilitySuccess = useCallback(() => {
    if (job?.is_interest) setAction('interest');
    else setAction('accept');
  }, [job?.is_interest]);

  const availabilityError = useCallback(() => {
    showErrorToast({
      description: globalLocalize('somethingWentWrong'),
      title: globalLocalize('error'),
    });
  }, []);

  const { addAvailableDay, isLoading: availabilityLoading } =
    useAddAvailableDay(availabilitySuccess, availabilityError);

  const onActionConfirmed = useCallback(() => {
    if (job) {
      switch (action) {
        case 'accept':
          acceptJob.mutate({ jobId: job.id });
          setAction(undefined);
          break;
        case 'refuse':
          refuseJob.mutate({ jobId: job.id });
          setAction(undefined);
          break;
        case 'interest':
          interestInJob.mutate({ jobId: job.id });
          setAction(undefined);
          break;
        default:
          break;
      }
    }
  }, [job, action, acceptJob, refuseJob, interestInJob]);

  if (job) {
    headerTitle = `${formatJobDurationDisplay(job.start, job.end, '')}
    ${job.organisation}
    (${job.travel_distance?.toFixed(1) || '?'} km) `;
    salaryDisplay = formatRates(
      job.max_rate,
      job.max_rate_incl,
      job.max_rate_exempt,
      job.actual_rate,
      job.actual_rate_incl,
      job.actual_rate_exempt,
      vatExempt,
    );
  }

  const interestedBtnClassName = twMerge(
    'w-42 flex items-center',
    'mr-1 justify-center py-2 text-white bg-lightBlue',
    'hover:bg-darkBlue rounded-l-md border-slate300 rounded-r-none',
    job?.is_interested && 'cursor-not-allowed',
  );
  if (!job) return <></>;
  return (
    <div className="container flex flex-col w-full my-1 px-0 cursor">
      <Panel
        Header={CardHeader}
        headerTitle={headerTitle}
        isJobPersonal={job?.is_personal}
        isJobaChoice={job?.is_interest}
        isDerivedJob={job?.is_derived}
        isAvailable={job?.is_available}
        headerClassName="bg-gray50 hover:bg-gray75"
      >
        <InfoItem
          textInfo={`${job?.location}, ${job?.full_address}`}
          iconName="locationN"
          textClassName="text-lightBlue"
          isLink
          url={`https://maps.google.com/?q=${job.full_address}`}
        />
        <InfoItem
          textInfo={job?.parking_free}
          iconName="local_parking"
          textClassName="text-purple"
        />
        <InfoItem
          textInfo={formatDateToLocaleNetherlands(job.date)}
          iconName="calendar"
          textClassName="text-purple"
        />
        <InfoItem
          textInfo={formatJobDurationDisplay(
            job.start,
            job.end,
            job.pause.toString(),
          )}
          iconName="clock"
        />
        <InfoItem
          textInfo={job.competence}
          iconName="wrench"
          textClassName={salaryDisplay.useHighlight ? 'text-destructive' : ''}
        />
        <InfoItem
          iconName="license-1"
          textInfo={
            job.certificates.length > 0
              ? job.certificates.join(', ')
              : localize('noCertificatesRequired')
          }
        />
        <InfoItem
          textInfo={salaryDisplay.displayText}
          iconName="euro"
          textClassName={
            salaryDisplay.useHighlight ? 'text-brown text-body-medium-bold' : ''
          }
        />
        <InfoItem
          textInfo={job.remarks && job.remarks !== '' ? job.remarks : '-'}
          iconName="message1"
        />
        <div className="flex flex-row items-center justify-end mt-4 mr-5 mb-3">
          {job.accept_now ? (
            <Button
              label={localize('accept')}
              disabled={BtnDisabled}
              variant="primary"
              className="w-42 flex items-center mr-1 justify-center py-2 text-white bg-lightBlue hover:bg-darkBlue rounded-l-md border-slate300 rounded-r-none"
              onClick={() => {
                setBtnDisabled(true);
                if (job.is_available) setAction('accept');
                else setAction('setAvailability');
                // acceptJob.mutate({ jobId: job.id });
              }}
            />
          ) : (
            <Button
              label={localize('interested')}
              disabled={job.is_interested}
              variant="primary"
              className={interestedBtnClassName}
              onClick={() => {
                if (job.is_available) setAction('interest');
                else setAction('setAvailability');
                // acceptJob.mutate({ jobId: job.id });
              }}
            />
          )}

          <Button
            label={localize('toRefuse')}
            variant="outlined"
            className="w-32 flex items-center justify-center py-2 hover:bg-gray100 border-slate300 mr-1 rounded-none"
            onClick={() => {
              setAction('refuse');
              // refuseJob.mutate({ jobId: job.id });
            }}
          />
          <Button
            label={localize('details')}
            variant="outlined"
            className="w-32 flex items-center justify-center py-2 hover:bg-gray100 rounded-r-md rounded-l-none border-slate300"
            onClick={() => {
              window.location.assign(`/freelancer/job/${job.id}/`);
            }}
          />
        </div>
      </Panel>
      <ConfirmationDialog
        onConfirm={
          job.is_available || action === 'refuse'
            ? onActionConfirmed
            : () => {
                addAvailableDay({ date: job.date });
              }
        }
        isOpen={!!action}
        isLoading={availabilityLoading}
        onCloseClicked={() => {
          setAction(undefined);
          setBtnDisabled(false);
        }}
        message={classNames(
          action === 'setAvailability' &&
            localize('setAvailability', {
              date: formatDateToLocaleNetherlands(job.date),
            }),
          action === 'accept' && localize('confirmAccept'),
          action === 'refuse' && localize('confirmRefuse'),
          action === 'interest' && localize('confirmInterest'),
        )}
      />
    </div>
  );
};

export default JobCard;
